// src/pages/EstadisticasPage.tsx
import { useEffect, useState } from 'react'
import api from '../services/api'
import { BarChart3, TrendingUp, Users, Package, DollarSign, Calendar } from 'lucide-react'

interface VendedorStats {
  id_vendedor: number
  nombre_vendedor: string
  rol_vendedor: string
  total_ventas: number
  total_clientes: number
  total_facturado: number
  subtotal_0: number
  subtotal_12: number
  total_iva: number
  total_descuentos: number
  ventas_canceladas: number
}

interface GananciaVendedor {
  id_vendedor: number
  nombre_vendedor: string
  rol_vendedor: string
  total_vendido: number
  costo_total: number
  ganancia_bruta: number
  descuentos_aplicados: number
  iva_a_pagar: number
  ganancia_neta_real: number
  total_ventas: number
  ticket_promedio: number
}

interface ProductoStats {
  id_producto: number
  detalle: string
  codigo_barras: string
  costo_produccion: number
  tarifa_iva: string
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  veces_vendido: number
  unidades_vendidas: number
  total_ventas: number
  precio_promedio_venta: number
  costo_total: number
  iva_a_pagar: number
  descuento_proporcional: number
  ganancia_neta_real: number
}

interface ResumenGeneral {
  ventas: {
    total_ventas: number
    total_facturado: number
    total_iva: number
    total_descuentos: number
    ticket_promedio: number
    ventas_canceladas: number
  }
  ganancias: {
    total_vendido: number
    costo_total: number
    ganancia_bruta: number
  }
  productos: {
    total_productos: number
    productos_descontinuados: number
    productos_sin_costo: number
    unidades_en_stock: number
  }
  clientes: {
    total_clientes: number
  }
  vendedores: {
    total_vendedores: number
    administradores: number
    distribuidores: number
  }
}

export function EstadisticasPage() {
  const [loading, setLoading] = useState(true)
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  
  const [resumen, setResumen] = useState<ResumenGeneral | null>(null)
  const [vendedores, setVendedores] = useState<VendedorStats[]>([])
  const [ganancias, setGanancias] = useState<GananciaVendedor[]>([])
  const [productos, setProductos] = useState<ProductoStats[]>([])

  const fetchEstadisticas = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (fechaInicio) params.append('fecha_inicio', fechaInicio)
      if (fechaFin) params.append('fecha_fin', fechaFin)

      const [resumenRes, vendedoresRes, gananciasRes, productosRes] = await Promise.all([
        api.get(`/estadisticas/resumen-general?${params}`),
        api.get(`/estadisticas/ventas-por-vendedor?${params}`),
        api.get(`/estadisticas/ganancias-por-vendedor?${params}`),
        api.get(`/estadisticas/productos-mas-vendidos?${params}&limit=10`)
      ])

      setResumen(resumenRes.data)
      setVendedores(vendedoresRes.data.vendedores || [])
      setGanancias(gananciasRes.data.ganancias || [])
      setProductos(productosRes.data.productos || [])
    } catch (error) {
      console.error('Error cargando estadísticas:', error)
      alert('Error al cargar estadísticas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEstadisticas()
  }, [])

  const handleFiltrar = () => {
    fetchEstadisticas()
  }

  const handleLimpiarFiltros = () => {
    setFechaInicio('')
    setFechaFin('')
    setTimeout(() => fetchEstadisticas(), 100)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📊 Estadísticas Administrativas</h1>
          <p className="text-sm text-gray-500 mt-1">Panel de análisis de ventas y ganancias</p>
        </div>
      </div>

      {/* Filtros de Fecha */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Fecha Inicio
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Fecha Fin
            </label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleFiltrar}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Filtrar
          </button>
          <button
            onClick={handleLimpiarFiltros}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Limpiar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Cargando estadísticas...</p>
        </div>
      ) : (
        <>
          {/* Resumen General */}
          {resumen && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Total Ventas</p>
                    <p className="text-3xl font-bold mt-1">{resumen.ventas.total_ventas}</p>
                    <p className="text-blue-100 text-xs mt-2">Ticket: ${resumen.ventas.ticket_promedio?.toFixed(2) || '0.00'}</p>
                  </div>
                  <BarChart3 className="w-12 h-12 text-blue-200" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Total Facturado</p>
                    <p className="text-3xl font-bold mt-1">${resumen.ventas.total_facturado?.toFixed(2) || '0.00'}</p>
                    <p className="text-green-100 text-xs mt-2">IVA: ${resumen.ventas.total_iva?.toFixed(2) || '0.00'}</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-green-200" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Ganancia Bruta</p>
                    <p className="text-3xl font-bold mt-1">
                      {resumen.ganancias.costo_total === 0 || resumen.ganancias.costo_total === null ? (
                        <span className="text-2xl">⚠️ No calculable</span>
                      ) : (
                        `$${resumen.ganancias.ganancia_bruta?.toFixed(2) || '0.00'}`
                      )}
                    </p>
                    <p className="text-purple-100 text-xs mt-2">
                      {resumen.ganancias.costo_total === 0 || resumen.ganancias.costo_total === null ? (
                        'Sin costos configurados'
                      ) : (
                        `Costo: $${resumen.ganancias.costo_total?.toFixed(2) || '0.00'}`
                      )}
                    </p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-purple-200" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Productos / Vendedores</p>
                    <p className="text-3xl font-bold mt-1">{resumen.productos.total_productos} / {resumen.vendedores.total_vendedores}</p>
                    <p className="text-orange-100 text-xs mt-2">Clientes: {resumen.clientes.total_clientes}</p>
                  </div>
                  <Package className="w-12 h-12 text-orange-200" />
                </div>
              </div>
            </div>
          )}

          {/* Alerta de productos sin costo */}
          {resumen && resumen.productos.productos_sin_costo > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-2xl">⚠️</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {resumen.productos.productos_sin_costo} producto(s) sin costo de producción
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    Las estadísticas de ganancias no son precisas porque {resumen.productos.productos_sin_costo} producto(s) 
                    no tienen configurado el <strong>costo de producción</strong>. 
                    Por favor, configure este valor en el módulo de Productos para obtener cálculos correctos de rentabilidad.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Ventas por Vendedor */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-primary-600" />
              Ventas por Vendedor
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clientes</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">IVA</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Canceladas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vendedores.map((v) => (
                    <tr key={v.id_vendedor} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{v.nombre_vendedor}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          v.rol_vendedor === 'ADMINISTRADOR' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {v.rol_vendedor}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{v.total_ventas}</td>
                      <td className="px-4 py-3 text-right">{v.total_clientes}</td>
                      <td className="px-4 py-3 text-right font-bold text-green-600">${v.total_facturado?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">${v.total_iva?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{v.ventas_canceladas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ganancias por Vendedor */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-green-600" />
              Ganancias por Vendedor
            </h2>
            {ganancias.some(g => g.costo_total === 0 || g.costo_total === null) && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Atención:</strong> Algunos productos no tienen costo de producción configurado. 
                  Las ganancias mostradas pueden no ser precisas. Configure el costo de producción en el módulo de productos.
                </p>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Vendido</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Costo Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ganancia Bruta</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Descuentos</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-blue-50">IVA a Pagar</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-green-50">Ganancia Real</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margen %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {ganancias.map((g) => {
                    const sinCosto = !g.costo_total || g.costo_total === 0
                    const margen = g.total_vendido > 0 && !sinCosto ? ((g.ganancia_neta_real / g.total_vendido) * 100) : 0
                    return (
                      <tr key={g.id_vendedor} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{g.nombre_vendedor}</td>
                        <td className="px-4 py-3 text-right">${g.total_vendido?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-red-600">
                          {sinCosto ? (
                            <span className="text-xs text-gray-400 italic">Sin costo</span>
                          ) : (
                            `$${g.costo_total?.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600">
                          {sinCosto ? (
                            <span className="text-xs text-gray-400 italic">N/A</span>
                          ) : (
                            `$${g.ganancia_bruta?.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-orange-600">${g.descuentos_aplicados?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-blue-600 bg-blue-50">
                          ${g.iva_a_pagar?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-green-700 bg-green-50">
                          {sinCosto ? (
                            <span className="text-xs text-gray-400 italic">No calculable</span>
                          ) : (
                            `$${g.ganancia_neta_real?.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {sinCosto ? (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium" title="Producto sin costo de producción configurado">
                              ⚠️ Sin costo
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              margen >= 30 
                                ? 'bg-green-100 text-green-800' 
                                : margen >= 15 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {margen.toFixed(1)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Productos Más Vendidos */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-600" />
              Top 10 Productos Más Vendidos
            </h2>
            {productos.some(p => !p.costo_produccion || p.costo_produccion === 0) && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Algunos productos no tienen costo de producción.</strong> 
                  No se puede calcular ganancia real.Onfigure el costo en cada producto.
                </p>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unidades</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Veces Vendido</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Ventas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Costo Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-blue-50">IVA a Pagar</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase bg-green-50">Ganancia Real</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margen %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {productos.map((p, idx) => {
                    const sinCosto = !p.costo_produccion || p.costo_produccion === 0
                    const margen = p.total_ventas > 0 && !sinCosto ? ((p.ganancia_neta_real / p.total_ventas) * 100) : 0
                    return (
                      <tr key={p.id_producto} className={`hover:bg-gray-50 ${sinCosto ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-400">#{idx + 1}</span>
                            <div>
                              <div className="font-medium">
                                {p.detalle}
                                {sinCosto && <span className="ml-2 text-xs text-yellow-600">⚠️</span>}
                              </div>
                              <div className="text-xs text-gray-400">{p.codigo_barras || 'Sin código'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold">{p.unidades_vendidas}</td>
                        <td className="px-4 py-3 text-right">{p.veces_vendido}</td>
                        <td className="px-4 py-3 text-right font-bold text-blue-600">${p.total_ventas?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-red-600">
                          {sinCosto ? (
                            <span className="text-xs text-gray-400 italic">Sin costo</span>
                          ) : (
                            `$${p.costo_total?.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-600 bg-blue-50">
                          ${p.iva_a_pagar?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-green-700 bg-green-50">
                          {sinCosto ? (
                            <span className="text-xs text-gray-400 italic" title="No se puede calcular ganancia sin costo de producción">
                              No calculable
                            </span>
                          ) : (
                            `$${p.ganancia_neta_real?.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {sinCosto ? (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium" title="Configure el costo de producción de este producto">
                              Sin costo prod.
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              margen >= 30 
                                ? 'bg-green-100 text-green-800' 
                                : margen >= 15 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {margen.toFixed(1)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
